import React, { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { useDebouncedCallback } from "use-debounce";
import { Link } from "react-router-dom";
import moment from "moment-timezone";
import { useMediaQuery } from "react-responsive";
import Grid from "@mui/material/Grid";
import ReactModal from "react-modal";

import axios from "axios";
import ai from "../../helpers/axios";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Search from "../search";
import Loader from "../loader";
import currencyFormatter from "../../helpers/currencyFormatter";

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: action.payload.loadingStatus,
      };
    }
    case "dataDownloaded": {
      return {
        ...state,
        loading: false,
        data: action?.payload?.data?.data || [],
        totalLength: action?.payload?.data?.countTotal,
      };
    }
    case "search": {
      return {
        ...state,
        query: action.query,
        currentPage: 1,
      };
    }
    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }
    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }
    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
        currentPage: 1,
      };
    }
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    default:
      break;
  }
  return state;
};

const SingleTripQuotesDetailsLegData = ({ legs }) => {
  return (
    <div className="lead-quotes-nested-table">
      <h6>Leg Data</h6>

      <table>
        <tbody>
        {!legs.length && <tr>
          <td colSpan={3}>No legs exists.</td>
        </tr>}
        {legs?.map((item, index) => (
          <tr key={item._id}>
            <td>Leg {index + 1}: {moment.utc(item.departureDate).format("M/D/YY")} {item.departureAirport?.code}-{item.arrivalAirport?.code}</td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );
};

const SingleTripQuotesDetailsAircraftData = ({ aircraftOption }) => {
  if (!aircraftOption) {
    return null;
  }

  return (
    <div className="lead-quotes-nested-table">
      <h6>Aircraft Data</h6>

      <table>
        <tbody>
        <tr>
          <td>YOM: {aircraftOption.aircraft?.yom || "N/A"}</td>
        </tr>
        <tr>
          <td>Refurb: {aircraftOption.interiorRefurbYear || "N/A"}</td>
        </tr>
        <tr>
          <td>OPI: {aircraftOption.aircraft?.opi || "N/A"}</td>
        </tr>
        <tr>
          <td>Seats: {aircraftOption.aircraft?.seats || "N/A"}</td>
        </tr>
        </tbody>
      </table>
    </div>
  );
};

const SingleTripQuotesDetailsCheckboxes = ({ aircraftOption }) => {
  const checkboxes = useMemo(() => {
    const { safetyStandards, amenities, importantNotesList } = aircraftOption || {};

    return [...safetyStandards || [], ...amenities || [], ...importantNotesList || []].filter(Boolean);
  }, [aircraftOption]);

  if (!aircraftOption) {
    return null;
  }

  return (
    <div className="lead-quotes-nested-table">
      <h6>Checkboxes</h6>

      <table>
        <tbody>
        {checkboxes.map(item => (
          <tr key={item}>
            <td>{item}</td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );
};

const SingleTripQuotesDetailsNotes = ({ notes }) => {
  return (
    <div className="lead-quotes-nested-table">
      <h6>Notes</h6>

      <table>
        <tbody>
        <tr>
          <td>{notes || ""}</td>
        </tr>
        </tbody>
      </table>
    </div>
  );
};

const SingleTripQuotesDetails = ({ tripId, leadId, readOnly, data }) => {
  const [showDuplicateQuoteModal, setShowDuplicateQuoteModal] = useState(false);
  const [showCreateContractModal, setShowCreateContractModal] = useState(false);

  const handleDuplicateQuoteButton = useCallback(() => {
    setShowDuplicateQuoteModal(true);
  }, []);

  const handleCloseDuplicateQuoteModal = useCallback(() => {
    setShowDuplicateQuoteModal(false);
  }, []);

  const handleDuplicateQuote = useCallback(() => {
    window.open(`/quotes/${data._id}/duplicate/${data.aircraftOption?._id}`, "_blank");
    setShowDuplicateQuoteModal(false);
  }, [data]);

  const handleDuplicateLeadQuote = useCallback(() => {
    window.open(`/quotes/${data._id}/duplicate/${data.aircraftOption?._id}/${leadId}`, "_blank");
    setShowDuplicateQuoteModal(false);
  }, [data, leadId]);

  const handleCreateContractButton = useCallback(() => {
    setShowCreateContractModal(true);
  }, []);

  const handleCloseCreateContractModal = useCallback(() => {
    setShowCreateContractModal(false);
  }, []);

  const handleCreateContract = useCallback(() => {
    window.open(`/contracts/new/${leadId}/${data._id}/${data.aircraftOption?._id}/new-lead`, "_blank");
    setShowCreateContractModal(false);
  }, [data, leadId]);

  const handleCreateTripContract = useCallback(() => {
    window.open(`/trips/${tripId}/contracts/new/${data._id}/${data.aircraftOption?._id}/${leadId}`, "_blank");
    setShowCreateContractModal(false);
  }, [data, tripId, leadId]);

  return (
    <Grid container spacing={0}>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <SingleTripQuotesDetailsLegData legs={data.legs}/>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <SingleTripQuotesDetailsAircraftData aircraftOption={data.aircraftOption}/>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <SingleTripQuotesDetailsCheckboxes aircraftOption={data.aircraftOption}/>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <SingleTripQuotesDetailsNotes notes={data.aircraftOption.notes}/>
      </Grid>

      {!readOnly && !!leadId && (
        <Grid item xs={12}>
          <button
            onClick={handleDuplicateQuoteButton}
            className="base-button evo-blue mt-2 mb-2 mr-10"
          >
            Duplicate Quote
          </button>

          <button
            onClick={handleCreateContractButton}
            className="base-button evo-blue mt-2 mb-2"
          >
            Create Contract
          </button>

          <ReactModal
            isOpen={showDuplicateQuoteModal}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
            onRequestClose={handleCloseDuplicateQuoteModal}
            style={{
              overlay: {
                zIndex: 999999,
                backgroundColor: "rgb(32 53 68 / 50%)",
              },
              content: {
                width: "auto",
                border: "none",
                overflowY: "scroll",
                maxHeight: "100vh",
                minWidth: "35vw",
                borderRadius: "0",
                boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
              },
            }}
            appElement={document.getElementById("app")}
          >
            <div className="modal-header base-modal">
              <h4>Duplicate Quote</h4>
            </div>

            <div className="modal-body base-modal overflow-scroll">
              <p className="mt-30">Where should the new Quote go? You can keep it under the same Lead or create a new
                Lead.</p>

              <div className="w-100 mt-30 d-flex gap-20">
                <button className="base-button evo-blue flex-half" type="button" onClick={handleDuplicateLeadQuote}>
                  Existing Lead
                </button>

                <button className="base-button evo-blue flex-half" type="button" onClick={handleDuplicateQuote}>
                  New Lead
                </button>

                <button className="base-button btn-danger flex-half" type="button"
                        onClick={handleCloseDuplicateQuoteModal}>
                  Cancel
                </button>
              </div>
            </div>
          </ReactModal>

          <ReactModal
            isOpen={showCreateContractModal}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
            onRequestClose={handleCloseCreateContractModal}
            style={{
              overlay: {
                zIndex: 999999,
                backgroundColor: "rgb(32 53 68 / 50%)",
              },
              content: {
                width: "auto",
                border: "none",
                overflowY: "scroll",
                maxHeight: "100vh",
                minWidth: "35vw",
                borderRadius: "0",
                boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
              },
            }}
            appElement={document.getElementById("app")}
          >
            <div className="modal-header base-modal">
              <h4>Create Contract from Quote</h4>
            </div>

            <div className="modal-body base-modal overflow-scroll">
              <p className="mt-30">You're creating a Contract from this Quote. Select where it should be linked:</p>
              <ul>
                <li>Existing Lead & Trip<br/>(The Contract will be linked to this Trip.)</li>
                <li>New Lead & Trip<br/>(We'll create a new Lead and Trip, and link the Contract to that Trip.)</li>
              </ul>

              <div className="w-100 mt-30 d-flex gap-20">
                <button className="base-button evo-blue flex-half" type="button" onClick={handleCreateTripContract}>
                  Existing Lead & Trip
                </button>

                <button className="base-button evo-blue flex-half" type="button" onClick={handleCreateContract}>
                  New Lead & Trip
                </button>

                <button className="base-button btn-danger flex-half" type="button"
                        onClick={handleCloseCreateContractModal}>
                  Cancel
                </button>
              </div>
            </div>
          </ReactModal>
        </Grid>
      )}
    </Grid>
  );
};

const SingleTripQuotes = ({ tripId, leadId, readOnly }) => {
  const initialState = {
    loading: true,
    data: [],
    totalLength: 0,
    query: "",
    countPerPage: 10,
    currentPage: 1,
    sortBy: "createdAt",
    sortDirection: "desc",
  };
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef(null);
  const isMobile = useMediaQuery({ query: "(max-width: 899px)" });

  const columns = useMemo(() => [
    {
      name: "Quote #",
      selector: "createdAt",
      sortable: true,
      cell: (row) => {
        return <Link to={`/quotes/${row._id}`} target="_blank" rel="noopener noreferrer">{row.quoteTag || "-"}</Link>;
      },
      width: "80px",
      omit: isMobile,
    },
    {
      name: "Sent at",
      selector: "sentAt",
      sortable: true,
      cell: (row) => {
        return row.sentAt ? moment(row.sentAt).format("M/D/YY - HHmm") : "-";
      },
      minWidth: "80px",
      maxWidth: "110px",
      omit: isMobile,
    },
    {
      name: "Synced at",
      selector: "syncedAt",
      sortable: true,
      cell: (row) => {
        return row.syncedAt ? moment(row.syncedAt).format("M/D/YY - HHmm") : "-";
      },
      minWidth: "80px",
      maxWidth: "110px",
      omit: isMobile,
    },
    {
      name: "First Leg",
      selector: "firstLeg.departureDate",
      sortable: true,
      minWidth: "80px",
      cell: (row) => {
        if (!row.firstLeg) {
          return "N/A";
        }

        return `${moment.utc(row.firstLeg.departureDate).format("M/D/YY")} ${row.firstLeg.departureAirport?.code || ""}-${row.firstLeg.arrivalAirport?.code || ""}`;
      },
      omit: isMobile,
    },
    {
      name: "Last Leg",
      selector: "lastLeg.departureDate",
      sortable: true,
      minWidth: "80px",
      cell: (row) => {
        if (!row.lastLeg) {
          return "N/A";
        }

        if (!row.legs?.length || row.legs?.length <= 1) {
          return "-";
        }

        return `${moment.utc(row.lastLeg.departureDate).format("M/D/YY")} ${row.lastLeg.departureAirport?.code || ""}-${row.lastLeg.arrivalAirport?.code || ""}`;
      },
      omit: isMobile,
    },
    {
      name: "Aircraft",
      selector: "aircraft.name",
      sortable: true,
      minWidth: "95px",
      cell: (row) => {
        return row.aircraft?.name || "N/A";
      },
      omit: isMobile,
    },
    {
      name: "Price",
      selector: "totalCost",
      sortable: true,
      minWidth: "100px",
      cell: (row) => {
        return currencyFormatter.format(row.totalCost || 0);
      },
      omit: isMobile,
    },
    {
      name: "Quote Info",
      selector: "mobile",
      sortable: false,
      cell: (row) => {
        return <div className="lead-quotes-mobile-data">
          <span className="material-icons lead-quotes-mobile-icon">tag</span>
          <Link to={`/quotes/${row._id}`} target="_blank" rel="noopener noreferrer">{row.quoteTag || "-"}</Link>
          <br/>
          <span className="material-icons lead-quotes-mobile-icon">send</span>
          {row.sentAt ? moment(row.sentAt).format("M/D/YY - HHmm") : "-"}
          <br/>
          <span className="material-icons lead-quotes-mobile-icon">sync</span>
          {row.syncedAt ? moment(row.syncedAt).format("M/D/YY - HHmm") : "-"}
          <br/>
          <span className="material-icons lead-quotes-mobile-icon">flight_takeoff</span>
          {moment.utc(row.firstLeg.departureDate).format("M/D/YY")} {row.firstLeg.departureAirport?.code || ""}-{row.firstLeg.arrivalAirport?.code || ""}
          <br/>
          <span className="material-icons lead-quotes-mobile-icon">flight_land</span>
          {moment.utc(row.lastLeg.departureDate).format("M/D/YY")} {row.lastLeg.departureAirport?.code || ""}-{row.lastLeg.arrivalAirport?.code || ""}
          <br/>
          <span className="material-icons lead-quotes-mobile-icon">flight</span>
          {row.aircraft?.name || "N/A"}
          <br/>
          <span className="material-icons lead-quotes-mobile-icon">attach_money</span>
          {currencyFormatter.format(row.totalCost || 0)}
        </div>;
      },
      omit: !isMobile,
    },
  ], [isMobile]);

  const getData = async (query, loadSilently) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let loadingStatus = !loadSilently;

    dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

    try {
      const token = await getAccessTokenSilently().catch((e) => {
        return loginWithRedirect();
      });

      const { data } = await ai.auth(token).get(`/api/trips/${tripId}/quotes`, {
        signal,
        params: {
          query: query.trim(),
          page: state.currentPage,
          countPerPage: state.countPerPage,
          sortBy: state.sortBy,
          sortDirection: state.sortDirection,
        },
      });

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
        },
      });
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error);
    }
  };

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const debouncedGetData = useDebouncedCallback(
    (query) => {
      getData(query);
    },
    500,
  );

  const handleSearch = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });

    debouncedGetData(query);
  };

  const handleSort = (column, sortDirection) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  };

  useEffect(() => {
    getData(state.query);
  }, [state.currentPage, state.countPerPage, state.sortBy, state.sortDirection]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1="Quotes"
        rightColumn={
          <div
            id="trip-quotes-actions-row"
            className="header-actions-row base-header-actions-row has-delete-btn opi-header"
          >
            <Search
              placeholder="Search"
              onSearch={handleSearch}
              query={state.query}
              className="lead-quotes-search"
            />
          </div>
        }
        extraClasses="compact-view"
      />

      <div
        id="trip-quotes"
        className="server-pagination-table-container trips-list-table-container lead-quotes-table data-table-compact-view shadow-none mt-2"
      >
        <StickyHeaderDataTable
          columns={columns}
          data={state.data}
          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[5, 10, 25]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.loading}
          progressComponent={<Loader/>}
          persistTableHead
          highlightOnHover
          responsive
          expandableRows
          expandableRowsComponent={<SingleTripQuotesDetails tripId={tripId} leadId={leadId} readOnly={readOnly}/>}
        />
      </div>
    </Fragment>
  );
};

export default SingleTripQuotes;
